import React, { useState } from "react";
import styled from "styled-components";
import { addSubscriber, isFirebaseConfigured } from "../../lib/firebase";
import { addStibeeSubscriber, isStibeeConfigured } from "../../lib/stibee";
import { FaRegEnvelope, FaRegCommentDots } from "react-icons/fa";
import { Button } from "./theme";

/**
 * 구독 신청 폼.
 *
 * 이메일과 전화번호는 서로 다른 신청 경로다. 각각 입력칸과 구독 버튼을 따로 두어
 * "둘 중 하나만 남기면 된다"는 걸 배치로 보여준다. 어느 버튼을 눌렀느냐에 따라
 * 그쪽 값만 검사하고 그쪽 값만 보낸다. 개인정보 수집 동의는 어느 쪽이든 필수다.
 *
 * 저장 순서는 Firestore 먼저, 스티비 나중이다. Firestore가 전체 명단의 원장이고
 * 스티비는 이메일을 남긴 사람만 받는 발송 도구이기 때문이다.
 * 그래서 스티비 등록이 실패해도 방문자의 신청 자체는 성공으로 처리한다 —
 * 명단은 이미 Firestore에 남았으니 나중에 손으로 채워 넣을 수 있다.
 * (실패 사유는 콘솔에만 남는다. 자세한 내용은 docs/newsletter.md)
 */

const TEXT = {
  ko: {
    title: "뉴스레터 구독",
    scope: "회사 소식이나 꼭 알려드려야 할 중요한 소식만 보내드려요.",
    emailTitle: "이메일로 받기",
    emailLabel: "이메일",
    emailPlaceholder: "you@example.com",
    emailSubmit: "이메일로 구독",
    phoneTitle: "문자로 받기",
    phoneLabel: "전화번호",
    phonePlaceholder: "010-0000-0000",
    phoneSubmit: "문자로 구독",
    consent: "개인정보 수집 및 이용에 동의합니다.",
    consentDetail:
      "수집 항목 이메일 또는 전화번호 · 이용 목적 뉴스레터 발송 · 보유 기간 구독 해지 시까지. 동의를 거부할 수 있으나 이 경우 뉴스레터를 받아보실 수 없습니다.",
    submitting: "신청 중",
    success: "구독 신청이 완료되었습니다.",
    successNote: "새 뉴스레터가 나오면 알려드릴게요.",
    successNoteConfirm:
      "보내드린 확인 메일에서 링크를 눌러 주세요. 그래야 구독이 끝납니다.",
    errorEmailEmpty: "이메일 주소를 입력해 주세요.",
    errorEmail: "올바른 이메일 주소를 입력해 주세요.",
    errorPhoneEmpty: "전화번호를 입력해 주세요.",
    errorPhone: "전화번호는 숫자 10~11자리로 입력해 주세요.",
    errorConsent: "개인정보 수집 및 이용에 동의해 주세요.",
    errorGeneric: "신청에 실패했습니다. 잠시 후 다시 시도해 주세요.",
    errorConfig:
      "구독 기능이 아직 설정되지 않았습니다. official@basakansoft.com 으로 연락 주세요.",
  },
  en: {
    title: "Subscribe",
    scope: "We only send company news and important announcements.",
    emailTitle: "By email",
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    emailSubmit: "Subscribe by email",
    phoneTitle: "By text message",
    phoneLabel: "Phone",
    phonePlaceholder: "010-0000-0000",
    phoneSubmit: "Subscribe by text",
    consent: "I agree to the collection and use of my personal information.",
    consentDetail:
      "Collected: email or phone · Purpose: sending the newsletter · Retention: until you unsubscribe. You may decline, but then we cannot send you the newsletter.",
    submitting: "Submitting",
    success: "You're subscribed.",
    successNote: "We'll let you know when the next issue is out.",
    successNoteConfirm:
      "One more step: open the confirmation email we just sent and click the link.",
    errorEmailEmpty: "Enter an email address.",
    errorEmail: "Please enter a valid email address.",
    errorPhoneEmpty: "Enter a phone number.",
    errorPhone: "Please enter 10-11 digits for the phone number.",
    errorConsent: "Please agree to the collection and use of personal information.",
    errorGeneric: "Something went wrong. Please try again in a moment.",
    errorConfig:
      "Subscriptions aren't set up yet. Please reach us at official@basakansoft.com.",
  },
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function SubscribeForm({ lang = "ko", source = "newsletter" }) {
  const t = TEXT[lang] || TEXT.ko;

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  // 동의는 칸마다 따로 받는다. 한 칸에서 체크했다고 다른 칸까지 체크되면 부자연스럽다.
  const [consent, setConsent] = useState({ email: false, phone: false });
  const [status, setStatus] = useState("idle"); // idle | submitting | error
  // 마지막으로 누른 버튼. 에러와 '신청 중' 표시를 그쪽 칸에만 붙이기 위해 기억한다.
  const [channel, setChannel] = useState(null); // "email" | "phone" | null
  const [error, setError] = useState("");
  // 완료는 폼 전체가 아니라 신청한 칸에만 표시한다. 이메일을 남긴 뒤 문자도
  // 이어서 남길 수 있어야 하므로 다른 칸은 그대로 열어 둔다.
  // email 은 "plain" | "confirm" | false. "confirm" 은 스티비가 신청을 받아준 경우로,
  // 주소록에 [구독 확인 이메일]이 켜져 있으면 확인 메일의 링크를 눌러야 '구독중'이 된다.
  const [done, setDone] = useState({ email: false, phone: false });

  const fail = (kind, message) => {
    setChannel(kind);
    setStatus("error");
    setError(message);
  };

  const submit = async (kind) => {
    const trimmedEmail = kind === "email" ? email.trim() : "";
    const digits = kind === "phone" ? phone.replace(/\D/g, "") : "";

    if (kind === "email") {
      if (!trimmedEmail) return fail(kind, t.errorEmailEmpty);
      if (!EMAIL_PATTERN.test(trimmedEmail)) return fail(kind, t.errorEmail);
    } else {
      if (!digits) return fail(kind, t.errorPhoneEmpty);
      if (digits.length < 10 || digits.length > 11) {
        return fail(kind, t.errorPhone);
      }
    }

    if (!consent[kind]) return fail(kind, t.errorConsent);

    setChannel(kind);
    setStatus("submitting");
    setError("");

    try {
      await addSubscriber({ email: trimmedEmail, phone: digits, source });

      let stibeeAccepted = false;

      if (trimmedEmail && isStibeeConfigured) {
        try {
          await addStibeeSubscriber({ email: trimmedEmail });
          stibeeAccepted = true;
        } catch (stibeeError) {
          // 이미 구독 중인 주소이거나 스티비 쪽 문제다. 어느 쪽이든 방문자가
          // 할 수 있는 일이 없고 신청은 이미 Firestore에 남았으므로 막지 않는다.
          console.warn(
            "[newsletter] 스티비 주소록 등록 실패:",
            stibeeError && stibeeError.message
          );
        }
      }

      setStatus("idle");
      setChannel(null);
      if (kind === "email") {
        setDone((d) => ({ ...d, email: stibeeAccepted ? "confirm" : "plain" }));
        setEmail("");
      } else {
        setDone((d) => ({ ...d, phone: true }));
        setPhone("");
      }
    } catch (err) {
      // 방문자에게는 다시 시도해 달라는 말밖에 할 수 없지만, 원인은 남겨야 고칠 수 있다.
      // Firestore 실패는 보통 err.code 로 갈린다 (permission-denied / unavailable 등).
      console.error("[newsletter] 구독 신청 저장 실패:", err && err.code, err);
      fail(
        kind,
        err && err.message === "FIREBASE_NOT_CONFIGURED"
          ? t.errorConfig
          : t.errorGeneric
      );
    }
  };

  const handleSubmit = (kind) => (event) => {
    event.preventDefault();
    submit(kind);
  };

  const busy = status === "submitting";
  const errorFor = (kind) =>
    status === "error" && channel === kind && error ? (
      <ErrorText role="alert">{error}</ErrorText>
    ) : null;

  // 동의 체크는 버튼 바로 위, 각 칸 안에 둔다. 상단에 따로 두면 눈에 안 들어와
  // 버튼을 누른 뒤에야 발견하게 된다. 동의 없이 눌렀을 때만 그 칸의 동의 행을 붉게 표시한다.
  const consentMissing = (kind) =>
    status === "error" && channel === kind && error === t.errorConsent;
  const consentBox = (kind) => (
    <ConsentRow $invalid={consentMissing(kind)}>
      <Checkbox
        id={`subscribe-consent-${kind}`}
        type="checkbox"
        checked={consent[kind]}
        onChange={(e) => {
          const checked = e.target.checked;
          setConsent((c) => ({ ...c, [kind]: checked }));
          if (checked && consentMissing(kind)) setStatus("idle");
        }}
        disabled={busy}
      />
      <ConsentLabel htmlFor={`subscribe-consent-${kind}`}>
        {t.consent}
        <ConsentDetail>{t.consentDetail}</ConsentDetail>
      </ConsentLabel>
    </ConsentRow>
  );

  return (
    <Panel>
      <Heading>{t.title}</Heading>
      <Scope>{t.scope}</Scope>

      <Channels>
        {done.email ? (
          <Channel as="div" role="status">
            <ChannelTitle>
              <FaRegEnvelope aria-hidden="true" />
              {t.emailTitle}
            </ChannelTitle>
            <DoneTitle>{t.success}</DoneTitle>
            <DoneNote>
              {done.email === "confirm" ? t.successNoteConfirm : t.successNote}
            </DoneNote>
          </Channel>
        ) : (
          <Channel onSubmit={handleSubmit("email")} noValidate>
            <ChannelTitle>
              <FaRegEnvelope aria-hidden="true" />
              {t.emailTitle}
            </ChannelTitle>
            <Label htmlFor="subscribe-email">{t.emailLabel}</Label>
            <Input
              id="subscribe-email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder={t.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
            {consentBox("email")}
            {errorFor("email")}
            <ChannelButton type="submit" disabled={busy}>
              {busy && channel === "email" ? t.submitting : t.emailSubmit}
            </ChannelButton>
          </Channel>
        )}

        {done.phone ? (
          <Channel as="div" role="status">
            <ChannelTitle>
              <FaRegCommentDots aria-hidden="true" />
              {t.phoneTitle}
            </ChannelTitle>
            <DoneTitle>{t.success}</DoneTitle>
            <DoneNote>{t.successNote}</DoneNote>
          </Channel>
        ) : (
          <Channel onSubmit={handleSubmit("phone")} noValidate>
            <ChannelTitle>
              <FaRegCommentDots aria-hidden="true" />
              {t.phoneTitle}
            </ChannelTitle>
            <Label htmlFor="subscribe-phone">{t.phoneLabel}</Label>
            <Input
              id="subscribe-phone"
              type="tel"
              name="phone"
              autoComplete="tel"
              inputMode="numeric"
              placeholder={t.phonePlaceholder}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={busy}
            />
            {consentBox("phone")}
            {errorFor("phone")}
            <ChannelButton type="submit" disabled={busy}>
              {busy && channel === "phone" ? t.submitting : t.phoneSubmit}
            </ChannelButton>
          </Channel>
        )}
      </Channels>


      {!isFirebaseConfigured ? <NotReady>{t.errorConfig}</NotReady> : null}
    </Panel>
  );
}

const Panel = styled.section`
  margin-top: 3rem;
  padding: 2.25rem 2rem 2rem;
  background: var(--nl-surface);
  border-radius: 14px;

  @media screen and (max-width: 600px) {
    padding: 1.75rem 1.25rem 1.5rem;
  }
`;

const Heading = styled.h2`
  font-size: 1.1875rem;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: -0.02em;
  color: var(--nl-ink);
`;


// 이메일 / 문자 두 경로를 나란히 둔다. 각 칸이 독립된 form 이라
// 어느 칸에서 Enter 를 쳐도 그 칸의 버튼이 눌린다.
const Channels = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1.5rem;

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Channel = styled.form`
  display: flex;
  flex-direction: column;
  padding: 1.25rem 1.25rem 1.125rem;
  background: #ffffff;
  border: 1px solid var(--nl-line);
  border-radius: 12px;
`;

const ChannelTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.875rem;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
  letter-spacing: -0.01em;
  color: var(--nl-ink);

  svg {
    flex-shrink: 0;
    width: 1.125rem;
    height: 1.125rem;
    color: var(--nl-accent);
  }
`;

const Label = styled.label`
  display: flex;
  align-items: baseline;
  gap: 0.375rem;
  margin-bottom: 0.4375rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nl-ink);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6875rem 0.875rem;
  border: 1px solid var(--nl-line-strong);
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.9375rem;
  color: var(--nl-ink);
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &::placeholder {
    color: var(--nl-ink-mute);
  }

  &:focus {
    outline: none;
    border-color: var(--nl-accent);
    box-shadow: 0 0 0 3px rgba(224, 165, 0, 0.16);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

// 한쪽 칸에만 에러가 떠도 두 버튼이 같은 높이에 놓이도록 바닥에 붙인다.
const ChannelButton = styled(Button)`
  width: 100%;
  margin-top: auto;
`;

// 입력칸과 버튼 사이에 상자로 놓아 시선이 버튼으로 가기 전에 반드시 지나가게 한다.
// 동의 없이 눌렀을 때는 테두리를 붉게 해 어디를 놓쳤는지 바로 보이게 한다.
const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  margin: 0.875rem 0;
  padding: 0.75rem 0.875rem;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? "#b03024" : "var(--nl-line-strong)")};
  border-radius: 8px;
  background: ${({ $invalid }) =>
    $invalid ? "rgba(176, 48, 36, 0.06)" : "var(--nl-surface)"};
  transition: border-color 0.18s ease, background 0.18s ease;
`;

const Checkbox = styled.input`
  flex-shrink: 0;
  width: 1.125rem;
  height: 1.125rem;
  margin-top: 0.1875rem;
  accent-color: var(--nl-ink);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--nl-accent);
    outline-offset: 2px;
  }
`;

const ConsentLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.6;
  color: var(--nl-ink);
  cursor: pointer;
`;

const ConsentDetail = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 400;
  line-height: 1.6;
  color: var(--nl-ink-mute);
`;


const ErrorText = styled.p`
  margin: 0 0 0.875rem;
  font-size: 0.8125rem;
  line-height: 1.6;
  color: #b03024;
`;

const NotReady = styled.p`
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--nl-line);
  font-size: 0.75rem;
  line-height: 1.7;
  color: var(--nl-ink-mute);
`;

// 칸 안에 들어가는 완료 표시. 제목(이메일로 받기) 아래에 놓인다.
const DoneTitle = styled.p`
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.5;
  color: var(--nl-ink);
`;

const DoneNote = styled.p`
  margin-top: 0.4375rem;
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--nl-ink-soft);
`;

// 제목 바로 아래 발송 범위 안내. 사이트에 올라오는 글이 전부 발송되는 건 아니라서
// 버튼을 누르기 전에 읽히게 둔다. 완료 안내와 같은 보조 문구 스타일을 쓴다.
const Scope = styled(DoneNote)``;

export default SubscribeForm;
